/**
 * CoachDashboard - Main dashboard page for coaches with schedule views
 *
 * Provides three views:
 * - Today: Current day classes with attendance tracking (existing behavior)
 * - This Week: Next 7 days grouped by day
 * - Upcoming: Next 30 days in a compact list
 */

import {
  Badge,
  Box,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react"
import type { ClassSessionResponse } from "@/client/types.gen"
import { useMemo, useState } from "react"
import {
  FiCalendar,
  FiClock,
  FiClipboard,
  FiList,
  FiMapPin,
  FiTrendingUp,
} from "react-icons/fi"

import { SessionRoster, TodaysClassCard } from "@/features/coach"
import { useTodaysClasses } from "@/hooks/api/useCoachClasses"
import {
  formatSessionTime,
  groupSessionsByDate,
  useCoachUpcomingSchedule,
  useCoachWeekSchedule,
} from "@/hooks/api/useCoachSchedule"

// ---------------------------------------------------------------------------
// Status badge color mapping
// ---------------------------------------------------------------------------

function statusColor(
  status: string | null | undefined,
): string {
  switch (status) {
    case "completed":
      return "green"
    case "cancelled":
      return "red"
    case "in_progress":
      return "yellow"
    case "rescheduled":
      return "orange"
    default:
      return "blue"
  }
}

// ---------------------------------------------------------------------------
// Stat card used in the summary row
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  icon,
}: {
  label: string
  value: number | string
  icon: React.ReactNode
}) {
  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      p={4}
      textAlign="center"
      minW="80px"
    >
      <Flex justify="center" mb={1} color="cyan.600">
        {icon}
      </Flex>
      <Text fontSize="2xl" fontWeight="bold" color="gray.800">
        {value}
      </Text>
      <Text fontSize="xs" color="gray.500" overflowWrap="break-word">
        {label}
      </Text>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Week view: classes grouped by day with cards
// ---------------------------------------------------------------------------

function WeekScheduleView({ onSelectSession }: { onSelectSession?: (id: string) => void }) {
  const { data, isLoading, error } = useCoachWeekSchedule()

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px" direction="column" gap={3}>
        <Spinner size="lg" color="cyan.600" />
        <Text color="gray.500">Loading this week&apos;s schedule...</Text>
      </Flex>
    )
  }

  if (error) {
    return (
      <VStack gap={4} py={8} textAlign="center">
        <Text color="red.500">Failed to load schedule</Text>
        <Text color="gray.500" fontSize="sm">Please try refreshing the page</Text>
      </VStack>
    )
  }

  const items = data?.items ?? []

  if (items.length === 0) {
    return (
      <VStack gap={4} py={10} textAlign="center">
        <Box
          w={14}
          h={14}
          borderRadius="full"
          bg="gray.100"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <FiCalendar size={28} color="gray" />
        </Box>
        <Heading size="sm" color="gray.600">
          No classes scheduled this week
        </Heading>
        <Text color="gray.500" fontSize="sm">
          Your upcoming classes will appear here once they are assigned.
        </Text>
      </VStack>
    )
  }

  const grouped = groupSessionsByDate(items)

  return (
    <VStack align="stretch" gap={6}>
      {grouped.map((group) => (
        <Box key={group.date}>
          <Text fontWeight="semibold" fontSize="md" color="gray.700" mb={3}>
            {group.dateLabel}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
            {group.sessions.map((s) => (
              <ScheduleCard key={s.id} session={s} onSelect={onSelectSession} />
            ))}
          </SimpleGrid>
        </Box>
      ))}
    </VStack>
  )
}

// ---------------------------------------------------------------------------
// Schedule card used in week view
// ---------------------------------------------------------------------------

function ScheduleCard({ session, onSelect }: { session: ClassSessionResponse; onSelect?: (id: string) => void }) {
  const time = formatSessionTime(session.session_start)
  const endTime = session.session_end
    ? formatSessionTime(session.session_end)
    : null

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="lg"
      p={4}
      transition="all 0.2s"
      _hover={{ borderColor: "cyan.300", shadow: "sm" }}
      cursor={onSelect ? "pointer" : undefined}
      onClick={onSelect ? () => onSelect(session.id) : undefined}
    >
      <Flex justify="space-between" align="flex-start" mb={2}>
        <Text fontWeight="semibold" color="gray.800">
          {session.class_name || "Unnamed Class"}
        </Text>
        <Badge colorPalette={statusColor(session.status)} size="sm">
          {session.status || "scheduled"}
        </Badge>
      </Flex>

      <Flex gap={4} color="gray.600" fontSize="sm" flexWrap="wrap">
        <Flex align="center" gap={1}>
          <FiClock size={13} />
          <Text>
            {time}
            {endTime ? ` - ${endTime}` : ""}
          </Text>
        </Flex>
        {session.location && (
          <Flex align="center" gap={1}>
            <FiMapPin size={13} />
            <Text>{session.location}</Text>
          </Flex>
        )}
      </Flex>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Upcoming (30 day) compact list view
// ---------------------------------------------------------------------------

function UpcomingScheduleView() {
  const { data, isLoading, error } = useCoachUpcomingSchedule()

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px" direction="column" gap={3}>
        <Spinner size="lg" color="cyan.600" />
        <Text color="gray.500">Loading upcoming schedule...</Text>
      </Flex>
    )
  }

  if (error) {
    return (
      <VStack gap={4} py={8} textAlign="center">
        <Text color="red.500">Failed to load schedule</Text>
        <Text color="gray.500" fontSize="sm">Please try refreshing the page</Text>
      </VStack>
    )
  }

  const items = data?.items ?? []

  if (items.length === 0) {
    return (
      <VStack gap={4} py={10} textAlign="center">
        <Box
          w={14}
          h={14}
          borderRadius="full"
          bg="gray.100"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <FiList size={28} color="gray" />
        </Box>
        <Heading size="sm" color="gray.600">
          No upcoming classes in the next 30 days
        </Heading>
        <Text color="gray.500" fontSize="sm">
          Your upcoming classes will appear here once they are assigned.
        </Text>
      </VStack>
    )
  }

  const grouped = groupSessionsByDate(items)

  return (
    <VStack align="stretch" gap={4}>
      {grouped.map((group) => (
        <Box key={group.date}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
            {group.dateLabel}
          </Text>
          <VStack align="stretch" gap={1}>
            {group.sessions.map((s) => (
              <UpcomingRow key={s.id} session={s} />
            ))}
          </VStack>
        </Box>
      ))}
    </VStack>
  )
}

function UpcomingRow({ session }: { session: ClassSessionResponse }) {
  const time = formatSessionTime(session.session_start)

  return (
    <Flex
      bg="white"
      borderWidth="1px"
      borderColor="gray.100"
      borderRadius="md"
      px={4}
      py={2}
      align="center"
      gap={4}
      fontSize="sm"
      _hover={{ bg: "gray.50" }}
    >
      <Text color="gray.600" minW="70px" fontWeight="medium">
        {time}
      </Text>
      <Text color="gray.800" flex={1} fontWeight="medium">
        {session.class_name || "Unnamed Class"}
      </Text>
      {session.location && (
        <Text color="gray.500" display={{ base: "none", md: "block" }}>
          {session.location}
        </Text>
      )}
      <Badge colorPalette={statusColor(session.status)} size="sm">
        {session.status || "scheduled"}
      </Badge>
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// Today view: existing behavior preserved
// ---------------------------------------------------------------------------

function TodayView({
  onSelectSession,
}: {
  onSelectSession: (id: string) => void
}) {
  const { data, isLoading, error } = useTodaysClasses()

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="200px" direction="column" gap={3}>
        <Spinner size="lg" color="blue.500" />
        <Text color="gray.600">Loading your classes...</Text>
      </Flex>
    )
  }

  if (error) {
    return (
      <VStack gap={4} py={8} textAlign="center">
        <Text color="red.500" fontSize="lg">
          Failed to load classes
        </Text>
        <Text color="gray.500">Please try refreshing the page</Text>
      </VStack>
    )
  }

  const classes = data?.classes || []

  if (classes.length === 0) {
    return (
      <VStack gap={4} py={10} textAlign="center">
        <Box
          w={16}
          h={16}
          borderRadius="full"
          bg="gray.100"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <FiCalendar size={32} color="gray" />
        </Box>
        <Heading size="md" color="gray.600">
          No classes scheduled for today
        </Heading>
        <Text color="gray.500">
          Check back later or contact the office if you believe this is an
          error.
        </Text>
      </VStack>
    )
  }

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
      {classes.map((classSession) => (
        <TodaysClassCard
          key={classSession.id}
          classSession={classSession}
          onClick={() => onSelectSession(classSession.id)}
        />
      ))}
    </SimpleGrid>
  )
}

// ---------------------------------------------------------------------------
// Main CoachDashboard component
// ---------------------------------------------------------------------------

export function CoachDashboard() {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  )

  // Fetch data for stats (today + week)
  const { data: todayData } = useTodaysClasses()
  const { data: weekData } = useCoachWeekSchedule()

  const stats = useMemo(() => {
    const todayClasses = todayData?.classes?.length ?? 0
    const weekClasses = weekData?.items?.length ?? 0
    const uniqueTemplates = new Set(
      (weekData?.items ?? []).map((s) => s.class_template_id),
    ).size

    return { todayClasses, weekClasses, uniqueTemplates }
  }, [todayData, weekData])

  // Show roster view when a session is selected
  if (selectedSessionId) {
    return (
      <Container maxW="4xl" py={{ base: 4, md: 8 }} px={{ base: 2, md: 4 }}>
        <SessionRoster
          sessionId={selectedSessionId}
          onBack={() => setSelectedSessionId(null)}
        />
      </Container>
    )
  }

  const todaysDate = todayData?.date
    ? new Date(todayData.date).toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      })

  return (
    <Container maxW="full">
      {/* Page header */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        pt={{ base: 4, md: 12 }}
        pb={{ base: 3, md: 6 }}
        flexWrap="wrap"
        gap={4}
      >
        <Box>
          <Heading
            size={{ base: "md", md: "lg" }}
            display="flex"
            alignItems="center"
            gap={2}
          >
            <FiClipboard />
            My Classes
          </Heading>
          <Flex align="center" gap={2} color="gray.600" mt={1}>
            <FiCalendar size={14} />
            <Text>{todaysDate}</Text>
          </Flex>
        </Box>
      </Flex>

      {/* Stats row */}
      <SimpleGrid columns={3} gap={3} mb={6} minChildWidth="100px">
        <StatCard
          label="Classes Today"
          value={stats.todayClasses}
          icon={<FiClipboard size={20} />}
        />
        <StatCard
          label="This Week"
          value={stats.weekClasses}
          icon={<FiCalendar size={20} />}
        />
        <StatCard
          label="Unique Classes"
          value={stats.uniqueTemplates}
          icon={<FiTrendingUp size={20} />}
        />
      </SimpleGrid>

      {/* Tab navigation */}
      <Tabs.Root defaultValue="today" variant="subtle">
        <Tabs.List mb={4}>
          <Tabs.Trigger value="today">Today</Tabs.Trigger>
          <Tabs.Trigger value="week">This Week</Tabs.Trigger>
          <Tabs.Trigger value="upcoming">Upcoming</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="today">
          <TodayView onSelectSession={setSelectedSessionId} />
        </Tabs.Content>

        <Tabs.Content value="week">
          <WeekScheduleView onSelectSession={setSelectedSessionId} />
        </Tabs.Content>

        <Tabs.Content value="upcoming">
          <UpcomingScheduleView />
        </Tabs.Content>
      </Tabs.Root>
    </Container>
  )
}
